<template>
  <div class="top-bar">
    <!-- Hamburger Menu (Mobile Only) -->
    <div v-if="isMobile" class="hamburger-menu" @click="toggleDrawer">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" width="24px" height="24px">
        <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
      </svg>
    </div>

    <!-- Logo (Desktop Only) -->
    <IconEasylink v-if="!isMobile" class="top-bar-logo" @click="navigateToHome" />

    <!-- Actions: Buttons and User Info -->
    <div class="top-bar-actions">
      <!-- Premium & Product Features (Desktop Only) -->
      <template v-if="!isMobile">
        <!-- <button class="premium-button" @click="navigateToPremium">Premium 会员</button> -->
        <!-- <div class="feature-button-wrapper" ref="featureDropdownRef">
          <button class="feature-button" @click="toggleFeatureDropdown">
            <h2 class="feature-button-text-h2">产品功能</h2>
            <IconDropdownArrow class="feature-dropdown-icon" stroke="#FFFFFF" :stroke-opacity="0.5" />
          </button>
          <FeatureDropdown v-if="showFeatureDropdown" />
        </div> -->
      </template>

      <!-- Login/User Info Section -->
      <template v-if="isLogin">
        <div class="user-container"> <!-- Shown if logged in, on both desktop and mobile -->
          <div class="cloud-space-wrapper">
            <button class="cloud-space-button" @click="handleCloudSpaceClick">
              云空间
            </button>
          </div>
          <div class="avatar-wrapper" ref="dropdownRef">
            <img class="avatar" :class="{ 'is-loading': isLoading }" :src="avatarUrl" @click="toggleDropdown"
              alt="用户头像" />
            <div v-if="showDropdown" class="dropdown-menu">
              <button class="menu-item" @click="handleChangeAvatar" :disabled="isLoading">
                {{ isLoading ? "加载中..." : "切换头像" }}
              </button>
              <button class="menu-item" @click="handleLogout">退出登录</button>
            </div>
          </div>
        </div>
      </template>
      <template v-else> <!-- Not logged in -->
        <button v-if="!isMobile" class="login-button" @click="handleLoginClick"> <!-- Desktop login -->
          登录
        </button>
        <button v-if="isMobile" class="mobile-login-button" @click="handleLoginClick"> <!-- Mobile login -->
          登录
        </button>
      </template>
    </div>

    <!-- Mobile Drawer -->
    <el-drawer v-model="isDrawerOpen" direction="ltr" :size="drawerSize" :with-header="false" :modal="true"
      :show-close="false" class="mobile-top-bar-drawer" @close="isDrawerOpen = false">
      <div class="drawer-content">
        <div class="drawer-menu-item" @click="navigateToHome">
          <IconHome class="drawer-menu-icon" fill="#NEW_COLOR_FOR_ICON_HOME" />
          <span>首页</span>
        </div>

        <!-- <div class="drawer-menu-item" @click="navigateToPremium">
          <IconVIP class="drawer-menu-icon" />
          <span>Premium 会员</span>
        </div> -->

        <!-- <div class="drawer-divider"></div>
        <h2 class="drawer-subheader">产品功能</h2>
        <div v-for="feature in features" :key="feature.title" class="drawer-menu-item feature-item"
          @click="handleFeatureClick(feature)">
          <component :is="feature.iconComponent" :fill="feature.iconColor" class="drawer-feature-icon" />
          <span>{{ feature.title }}</span>
        </div> -->
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, markRaw } from "vue";
import { useLogin } from "@/composables/login/useLogin";
import { useUserInfo } from "@/composables/user/useUserInfo";
import { useRouter } from "vue-router";
import Tracking from "@/model/tracking.js";
import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import FeatureDropdown from "./FeatureDropdown.vue";
import IconEasylink from "@/components/icons/IconEasylink.vue";

// Imports for drawer features (copied from FeatureDropdown.vue)
import IconLink from '@/components/icons/IconLink.vue';
import IconQRCode from '@/components/icons/IconQRCode.vue';
import IconAttachment from '@/components/icons/IconAttachment.vue';
import IconSpeed from '@/components/icons/IconSpeed.vue';
import IconPay from '@/components/icons/IconPay.vue';
import IconData from '@/components/icons/IconData.vue';
import IconHome from '@/components/icons/IconHome.vue';
import IconVIP from '@/components/icons/IconVIP.vue';

const router = useRouter();
const showDropdown = ref(false);
const dropdownRef = ref(null);

const showFeatureDropdown = ref(false);
const featureDropdownRef = ref(null);

const {
  state: { isLogin },
  action: { openLoginDialog, logout },
} = useLogin();

const {
  state: { isLoading },
  action: { generateNewAvatar, getAvatarUrl },
} = useUserInfo();

const avatarUrl = computed(() => getAvatarUrl());

// Mobile responsiveness
const isMobile = ref(false);
const isDrawerOpen = ref(false);

const px2rem = (px) => `${px / 16}rem`;
const drawerSize = computed(() => isMobile.value ? px2rem(280) : '0'); // Example size

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
  if (!isMobile.value && isDrawerOpen.value) {
    isDrawerOpen.value = false; // Close drawer if switching to desktop
  }
};

const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value;
};

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
  document.removeEventListener("click", handleClickOutside);
});


const handleCloudSpaceClick = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    object: "cloud-space-btn",
  });
  router.push("/cloudspace/files");
  if (isDrawerOpen.value) isDrawerOpen.value = false;
};

const handleLoginClick = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    object: "login-btn",
  });
  openLoginDialog();
};

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value;
  if (showDropdown.value) {
    Tracking.send({
      trigger: "button-click",
      page: "main-page",
      object: "avatar-dropdown-btn",
    });
  }
};

const handleLogout = async () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    object: "logout-btn",
  });
  await logout();
  showDropdown.value = false;
  if (isDrawerOpen.value) isDrawerOpen.value = false;
};

const handleChangeAvatar = () => {
  Tracking.send({
    trigger: "button-click",
    page: "main-page",
    object: "change-avatar-btn",
  });
  generateNewAvatar();
  showDropdown.value = false;
};

const toggleFeatureDropdown = () => {
  showFeatureDropdown.value = !showFeatureDropdown.value;
  if (showFeatureDropdown.value) {
    Tracking.send({
      trigger: "button-click",
      page: "main-page",
      object: "feature-dropdown-btn",
    });
  }
};

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    showDropdown.value = false;
  }
  if (featureDropdownRef.value && !featureDropdownRef.value.contains(event.target)) {
    showFeatureDropdown.value = false;
  }
  // Close drawer if clicked outside (optional, el-drawer might handle this with modal)
  // const drawerElement = document.querySelector('.mobile-top-bar-drawer');
  // if (isDrawerOpen.value && drawerElement && !drawerElement.contains(event.target)) {
  //   const hamburger = document.querySelector('.hamburger-menu');
  //   if (hamburger && !hamburger.contains(event.target)) {
  //      isDrawerOpen.value = false;
  //   }
  // }
};

// Drawer navigation/actions
const navigateToHome = () => {
  router.push('/'); // Assuming '/' is your home route
  isDrawerOpen.value = false;
  Tracking.send({ trigger: "drawer-menu-click", object: "home-link" });
};

const navigateToPremium = () => {
  router.push('/premium');
  if (isDrawerOpen.value) {
    isDrawerOpen.value = false;
  }
  Tracking.send({ trigger: "button-click", object: "premium-link" });
};

const handleFeatureClick = (feature) => {
  // Placeholder: Implement action for feature click
  console.log("Feature clicked:", feature.title);
  // Example: router.push(`/features/${feature.id}`);
  isDrawerOpen.value = false;
  Tracking.send({ trigger: "drawer-menu-click", object: `feature-${feature.title}` });
};

// Features data (copied from FeatureDropdown.vue)
const features = ref([
  {
    title: "文件转链接",
    description: "支持文件、网址、文本转成短链",
    iconBg: "#F3F2FE",
    iconColor: "#C8BEFE",
    iconComponent: markRaw(IconLink)
  },
  {
    title: "文件转二维码",
    description: "支持文件、网址、文本转成二维码",
    iconBg: "#FFF7EB",
    iconColor: "#FEC04D",
    iconComponent: markRaw(IconQRCode)
  },
  {
    title: "附件列表",
    description: "建立文件附件列表，挂在社交媒体上",
    iconBg: "#FFFFFF",
    iconColor: "#FFFFFF",
    hasBorder: true,
    iconComponent: markRaw(IconAttachment)
  },
  {
    title: "无限速下载",
    description: "享受急速文件下载体验，无需等待",
    iconBg: "#EFFAFF",
    iconColor: "#67CAF2",
    iconComponent: markRaw(IconSpeed)
  },
  {
    title: "付费解锁文件",
    description: "可以对二维码、链接定价并赚取收益",
    iconBg: "#FFEEEE",
    iconColor: "#FD504E",
    iconComponent: markRaw(IconPay)
  },
  {
    title: "数据统计",
    description: "轻松查看文件访问数据统计",
    iconBg: "#F1FBF3",
    iconColor: "#7CAD93",
    iconComponent: markRaw(IconData)
  },
]);

</script>

<style scoped lang="scss">
.top-bar {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: px2rem(64);
  z-index: 1001; // Ensure top-bar is above other content, drawer will be higher
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 px2rem(20); // Horizontal padding
  box-sizing: border-box;
  background-color: #1a1918; // Added to match dashboard
}

.top-bar-logo {
  width: px2rem(60);
  height: px2rem(30.59);
  margin-left: px2rem(10);
  cursor: pointer;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
}

.top-bar-actions {
  display: flex;
  align-items: center;
  gap: px2rem(40);
}

.hamburger-menu {
  display: none; // Hidden by default, shown in media query
  cursor: pointer;
  padding: px2rem(10); // Clickable area
  margin-left: px2rem(-10); // Align with edge
}

.premium-button {
  height: px2rem(30);
  padding: 0;
  background: linear-gradient(to right, #AEBFFF, #79CA9E);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  border: none;
  border-radius: px2rem(12);
  font-size: px2rem(13);
  font-family: "Source Han Sans CN", "思源黑体 Medium", sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s linear;

  &:hover {
    opacity: 0.8;
  }
}

.feature-button-wrapper {
  position: relative;
}

.feature-button {
  display: flex;
  align-items: center;
  gap: px2rem(8);
  height: px2rem(30);
  padding: 0;
  background: transparent;
  border: none;
  border-radius: px2rem(12);
  color: #ffffff;
  font-size: px2rem(13);
  font-family: "Source Han Sans CN", "思源黑体 Medium", sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s linear;

  .feature-dropdown-icon {
    width: px2rem(12);
    height: px2rem(5);
    flex-shrink: 0;
  }

  &:hover {
    opacity: 0.8;
  }
}

.login-button {
  width: px2rem(84);
  height: px2rem(30);
  background: transparent;
  border: px2rem(1) solid #2d7f6f;
  border-radius: px2rem(12);
  cursor: pointer;
  color: #ffffff;
  font-size: px2rem(13);
  font-family: "思源黑体 Medium";
  line-height: px2rem(19.5);
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transition: all 0.15s linear;
    box-shadow: 0 0 px2rem(20) rgba(45, 127, 111, 0.5);
  }
}

.user-container {
  display: flex;
  align-items: center;
  gap: px2rem(40); // Default gap for desktop
}

.cloud-space-wrapper {
  position: relative;
  display: inline-block;
}

.avatar-wrapper {
  display: flex;
  align-items: center;
  position: relative;
}

.avatar {
  width: px2rem(32);
  height: px2rem(32);
  border-radius: px2rem(12);
  cursor: pointer;
  background: #ffffff;
  object-fit: cover;
  border: px2rem(2) solid rgba(45, 127, 111, 0.2);
  box-shadow: inset 0 0 px2rem(2) rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &.is-loading {
    opacity: 0.6;
    cursor: wait;
    animation: pulse 1.5s ease-in-out infinite;
  }

  &:hover:not(.is-loading) {
    border-color: rgba(45, 127, 111, 0.4);
    box-shadow: 0 0 px2rem(20) rgba(45, 127, 111, 0.3),
      inset 0 0 px2rem(2) rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:active:not(.is-loading) {
    transform: translateY(0);
  }
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + px2rem(8));
  right: 0;
  background: #262626;
  border-radius: px2rem(12);
  padding: px2rem(8);
  z-index: 10; // Should be lower than drawer's z-index if they could overlap
  min-width: px2rem(120);
  box-shadow: 0 px2rem(4) px2rem(12) rgba(0, 0, 0, 0.15);

  .menu-item {
    width: 100%;
    height: px2rem(32);
    background: transparent;
    border: none;
    color: #ffffff;
    font-size: px2rem(13);
    font-family: "思源黑体 Regular";
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: px2rem(6);
    transition: all 0.2s ease;
    user-select: none;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    &:disabled {
      opacity: 0.6;
      cursor: wait;
    }
  }
}

@keyframes pulse {
  0% {
    opacity: 0.6;
  }

  50% {
    opacity: 0.3;
  }

  100% {
    opacity: 0.6;
  }
}

.cloud-space-button {
  display: flex;
  align-items: center;
  justify-content: center;
  height: px2rem(30);
  padding: px2rem(5) px2rem(29);
  background: transparent;
  border: px2rem(1) solid #2d7f6f;
  border-radius: px2rem(12);
  color: #ffffff;
  font-size: px2rem(13);
  font-family: "Source Han Sans CN", "思源黑体 Medium", sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s linear;

  &:hover {
    transition: all 0.15s linear;
    box-shadow: 0 0 px2rem(20) rgba(45, 127, 111, 0.5);
  }
}

/* Mobile Drawer Styles */
.mobile-top-bar-drawer {

  // Element Plus drawer is typically a high z-index (e.g., 2000+)
  // :deep(.el-drawer__body) can be used if needed for deeper customization
  .drawer-content {
    padding: px2rem(20);
    color: #fff; // Assuming dark theme for drawer
    background-color: #262626; // Match dropdown menu for consistency
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: px2rem(10);
  }

  .drawer-menu-item {
    padding: px2rem(12) px2rem(10);
    font-size: px2rem(14);
    cursor: pointer;
    border-radius: px2rem(6);
    transition: background-color 0.2s ease;
    display: flex;
    align-items: center;
    gap: px2rem(10);

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    &.login-drawer-item {
      // Optional: Special style for login button in drawer
      // background-color: #2d7f6f;
      // border: 1px solid #2d7f6f;
      // justify-content: center;
    }
  }

  .drawer-menu-icon {
    width: px2rem(16);
    height: px2rem(16);
  }

  .drawer-divider {
    height: px2rem(1);
    background-color: rgba(255, 255, 255, 0.1); // 深色分割线，两边顶到头
    margin: px2rem(10) 0; // 可根据需要调整上下间距
  }

  .drawer-feature-icon {
    width: px2rem(16);
    height: px2rem(16);
  }

  .drawer-subheader {
    font-size: px2rem(12);
    color: rgba(255, 255, 255, 0.7);
    margin-top: px2rem(10);
    margin-bottom: px2rem(5);
    padding-left: px2rem(10);
  }
}


/* Responsive adjustments */
@media (max-width: 768px) {
  .top-bar {
    padding-left: px2rem(10); // Reduced padding for mobile
    padding-right: px2rem(10);
  }

  .hamburger-menu {
    margin-left: px2rem(2);
    display: flex; // Show hamburger on mobile
    align-items: center;
  }

  .top-bar-actions {
    gap: px2rem(15); // Reduced gap for actions on mobile
  }

  .user-container {
    gap: px2rem(25); // Reduce gap in user section for mobile
  }

  .premium-button,
  .feature-button span,
  .login-button,
  // Desktop login button (hidden on mobile by v-if)
  .cloud-space-button {
    // Cloud space button (visible on mobile)
    font-size: px2rem(12);
  }

  .avatar {
    width: px2rem(28);
    height: px2rem(28);
  }

  .dropdown-menu {
    // Avatar dropdown menu
    min-width: px2rem(100);
    top: calc(100% + px2rem(6));
    padding: px2rem(6);

    .menu-item {
      height: px2rem(28);
      font-size: px2rem(12);
    }
  }
}

.mobile-login-button {
  width: px2rem(84); // Match original desktop button width
  height: px2rem(30); // Match original desktop button height
  padding: 0; // Remove padding as width and height are explicit
  background: transparent;
  border: px2rem(1) solid #2d7f6f; // Match desktop border
  border-radius: px2rem(12); // Match desktop border-radius
  cursor: pointer;
  color: #ffffff;
  font-size: px2rem(13); // Match original desktop button font size
  font-family: "思源黑体 Medium"; // Match desktop font-family
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s linear;

  &:hover { // Match desktop hover
    box-shadow: 0 0 px2rem(20) rgba(45, 127, 111, 0.5);
  }
}
</style>
